import { useId, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@resource-kit/ui';
import { Modal, ModalHeader, ModalTitle, ModalBody } from '@resource-kit/ui/react';
import { JsonDiffTable } from '@components/ui/JsonDiffTable';
import {
  CopyJsonButton,
  ExpandJsonButton,
  JsonTableToolbar,
} from '@components/ui/json-panel-chrome';
import { asJsonStructure } from '@lib/json-diff';

export interface JsonDiffPanelProps {
  before: unknown;
  after: unknown;
  /** Accessible name for the underlying table (and dialog title fallback). */
  ariaLabel?: string;
  /** Optional label shown left of the affordances above the table. */
  label?: ReactNode;
  title?: ReactNode;
  className?: string;
}

/**
 * `JsonPanel`'s sibling for a comparison: copy, fullscreen, and the
 * "changed only" switch, around a `JsonDiffTable`.
 *
 * The switch lives here rather than in the table for the same reason the
 * fullscreen button does — the table is rendered twice, inline and inside the
 * dialog, and one switch has to govern both. A table owning its own filter
 * state would show a dialog that silently disagrees with the panel behind it.
 */
export function JsonDiffPanel({
  before,
  after,
  ariaLabel,
  label,
  title,
  className,
}: JsonDiffPanelProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [changedOnly, setChangedOnly] = useState(true);
  const inlineSwitchId = useId();
  const dialogSwitchId = useId();
  const dialogTitle = title ?? label ?? ariaLabel ?? t('json.diff.tableAria');

  // What copy emits: both payloads verbatim, parsed so a column that arrived as
  // a JSON string does not come out double-encoded. A leaf table is not
  // pasteable JSON, which is the whole reason this escape hatch exists.
  const verbatim = useMemo(
    () => ({ before: asJsonStructure(before) ?? before, after: asJsonStructure(after) ?? after }),
    [before, after],
  );

  const changedOnlySwitch = (id: string) => (
    <label
      htmlFor={id}
      className="mr-1 inline-flex cursor-pointer items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground"
    >
      <input
        id={id}
        type="checkbox"
        checked={changedOnly}
        onChange={(event) => setChangedOnly(event.target.checked)}
        className="h-3 w-3 cursor-pointer accent-primary"
      />
      {t('json.diff.onlyChanged')}
    </label>
  );

  return (
    <div className={cn('space-y-1', className)}>
      <JsonDiffTable
        before={before}
        after={after}
        showChangedOnly={changedOnly}
        ariaLabel={ariaLabel}
        toolbar={
          <JsonTableToolbar label={label}>
            {changedOnlySwitch(inlineSwitchId)}
            <CopyJsonButton value={verbatim} />
            <ExpandJsonButton onClick={() => setOpen(true)} />
          </JsonTableToolbar>
        }
      />

      <Modal
        isOpen={open}
        onClose={() => setOpen(false)}
        size="full"
        className="flex h-[85vh] flex-col"
      >
        <ModalHeader onClose={() => setOpen(false)}>
          <div className="flex items-center gap-2">
            <ModalTitle>{dialogTitle}</ModalTitle>
            {changedOnlySwitch(dialogSwitchId)}
            <CopyJsonButton value={verbatim} />
          </div>
        </ModalHeader>
        <ModalBody className="min-h-0 flex-1 overflow-auto">
          {/* Same switch state, no second fullscreen button. */}
          <JsonDiffTable
            before={before}
            after={after}
            showChangedOnly={changedOnly}
            ariaLabel={ariaLabel}
          />
        </ModalBody>
      </Modal>
    </div>
  );
}

export default JsonDiffPanel;
